import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Provider, useSelector } from 'react-redux';
import { store } from './app/store';
import LoadingSpinner from "./LoadingSpinner";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from './components/ProtectedRoute';
import NotFound from "./pages/NotFound";
import { useIsMobile } from "./hooks/use-mobile";
import DesktopSidebarLayout from "./components/DesktopSidebarLayout";

// Import all pages directly (no lazy loading)
import GlassmorphicLandingPage from "./pages/GlassmorphicLandingPage";
import VoiceOrb from "./pages/VoiceOrb";
import Test from "./pages/Test";
import {LoginPage} from "./pages/Login";
import {SignUpPage} from "./pages/SignUp";
import Pricing from "./pages/Pricing";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ResumeUploader from "./pages/resume-builder/ResumeUploader";

// Protected pages
import AdminDashboard from "./pages/admin-dashboard/AdminDashboard";
import PhoneInterview from "./pages/PhoneInterview";
import InterviewSetupConfiguration from "./pages/interview-setup-configuration";
import GenerateQuestionsPage from "./pages/Generate-Questions";
import ChatInterview from "./pages/chat-interview";
import ResumeBuilder from "./pages/resume-builder";
import Setting from "./pages/setting/index";
import Help from "./pages/help/index";
import LiveInterviewSession from "./pages/live-interview-session";
import InterviewResultsFeedback from "./pages/interview-results-feedback";
import ProgressAnalyticsInsights from "./pages/progress-analytics-insights";
import PracticeLibraryResources from "./pages/practice-library-resources";
import DashboardHome from "./pages/dashboard-home";
import AboutUs from "./pages/AboutUs";
import Navbar from "./components/ui/Navbar";
import ContactUs from "./pages/Contact";
import UpcomingSessionsPlaner from "./pages/UpcomingSessionsPlaner";

import PhoneInterviewTest from "./pages/PhoneInterviewTest";


// import TechnicalInterview from "./pages/technical-interview/technical-interview";
import Tech from "./pages/technical-interview/TechApp";
import ChatInterviewTest from "./pages/ChatInterviewTest";
import OnboardingAdditionalInfo from "./pages/OnboardingAdditionalInfo";
import WhiteboardFlow from "./pages/technical-interview/WhiteboardFlow";
import DatabaseWhiteboard from "./pages/technical-interview/DatabaseWhiteboard";

// import ResumeBuilderNew from "./pages/Chatgpt-resume";

import SubscriptionCenter from "./pages/subscription/SubscriptionCenter";
import IssueReportPage from "./pages/IssueReportPage";
import Library from "./pages/Library";
import TaskProgressTrackerApp from "./pages/task-tracker/TasksProgressTracker";
import RankingsLeaderboardPage from "./pages/Gamified";
import PastInterviewsPage from "./pages/PastInterviewsPage";
import TranscriptAnalyzerTest from "./pages/transcript-analyzer-test/TranscriptAnalyzerTest";
import VideoAnalyzer from "./pages/transcript-analyzer-test/react_video_analysis";
import PDFEditor from "./pages/PDFEditor";


import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SalaryBoostPage from "./pages/SalaryBoostPage";
import QuestionsBank from "./pages/questions-bank/QuestionsBank";
import JobMatchAIPage from "./pages/JobMatchAIPage";


function ResponsiveNavbar() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const isAuthenticated =
    useSelector((state) => state?.auth?.isAuthenticated) ||
    Boolean(localStorage.getItem("access_token"));

  const publicPaths = new Set([
    "/",
    "/voice-orb",
    "/whats-app-clone",
    "/Test",
    "/login",
    "/sign-up",
    "/pricing",
    "/forgot-password",
    "/reset-password",
    "/upload-resume",
    "/voice-transcriber",
    "/about-us",
    "/contact-us",
    // "/phone-interview-test",
    // "/tech-interview-test",
    // "/chat-interview-test"
  ]);

  const isPublicRoute = publicPaths.has(location.pathname);

  if (!isMobile && isAuthenticated && !isPublicRoute) {
    return null;
  }

  return <Navbar />;
}

function App() {

  
  return (
    <BrowserRouter>
      <Provider store={store}>
        <ErrorBoundary>
          <ScrollToTop />
          <ResponsiveNavbar />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<GlassmorphicLandingPage />} />
            {/* <Route path="/voice-orb" element={<VoiceOrb />} /> */}
            <Route path="/Test" element={<Test />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sign-up" element={<SignUpPage />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/contact-us" element={<ContactUs />} />
            
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />    
                
            
            <Route path="/onboarding-additional-info" element={<OnboardingAdditionalInfo />} />
            
            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route element={<DesktopSidebarLayout />}>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/video-analysis" element={<VideoAnalyzer />} />
                
                {/* NEW ROUTES */}
                <Route path="/pdf-editor" element={<PDFEditor />}/>
                <Route path="/salaryboost" element={<SalaryBoostPage />} />
                <Route path="/leaderboard" element={<RankingsLeaderboardPage />} />
                <Route path="/whiteboard-flow" element={<WhiteboardFlow />} />
                <Route path="/whiteboard-database" element={<DatabaseWhiteboard />} />
                <Route path="/questions-bank" element={<QuestionsBank/>}/>
                <Route path="/job-match-ai" element={<JobMatchAIPage />} />
                <Route path="/upload-resume" element={<ResumeUploader />} />
                
                {/* <Route path="/phone-interview" element={<PhoneInterview />} /> */}
                <Route path="/interview-setup-configuration" element={<InterviewSetupConfiguration />} />
                <Route path="/generate-questions" element={<GenerateQuestionsPage />} />
                {/* <Route path="/chat-interview" element={<ChatInterview />} /> */}
                <Route path="/resume-builder" element={<ResumeBuilder />} />
                
                {/* New Resume Builder Route */}
                <Route path="/issue-report" element={<IssueReportPage />} />
                
                <Route path="/setting" element={<Setting />} />
                <Route path="/help" element={<Help />} />
                <Route path="/live-interview-session" element={<LiveInterviewSession />} />
                <Route path="/interview-results-feedback" element={<InterviewResultsFeedback />} />
                <Route path="/progress-analytics-insights" element={<ProgressAnalyticsInsights />} />
                <Route path="/practice-library-resources" element={<PracticeLibraryResources />} />
                <Route path="/dashboard-home" element={<DashboardHome />} />
                <Route path="/upcoming-sessions-planner" element={<UpcomingSessionsPlaner />} />
                
                {/*  routes for interview modules */}
                <Route path="/phone-interview" element={<PhoneInterviewTest />} />
                <Route path="/chat-interview" element={<ChatInterviewTest/>} />
                <Route path="/tech-interview" element={<Tech/>} />
                
                
                {/* subscription routes */}
                <Route path="/my-subscription" element={<SubscriptionCenter />} />
                <Route path="/my-subscription/usage" element={<SubscriptionCenter />} />
                <Route path="/my-invoices" element={<SubscriptionCenter />} />
                <Route path="/my-payments" element={<SubscriptionCenter />} />
                <Route path="/plans" element={<SubscriptionCenter />} />
                
                {/* TASKS PROGRESS TRACKING SYSTEM */}
                <Route path="/tasks-progress" element={<TaskProgressTrackerApp />} />
                
                <Route path="/past-interviews" element={<PastInterviewsPage />} />
                
                <Route path="/transcript-analyzer-test" element={<TranscriptAnalyzerTest />} />
                <Route path="/video-analyzer-test" element={<VideoAnalyzer />} />
                
                
                
              </Route>
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
      </Provider>
    </BrowserRouter>
  );
}

export default App;
